import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import { selectRegulationCategories, selectRegulations, selectRegulationStatus } from '../redux/regulationSlice';


function RegulationCard({ title, documentUrl, year }) {
  // Convert relative URL to absolute URL if needed
  const fullDocumentUrl = documentUrl?.startsWith('http')
    ? documentUrl
    : `https://gmk-web-api.chronopulse.com${documentUrl}`;
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        onClick={() => window.open(fullDocumentUrl, '_blank')}
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
          '&:hover': {
            boxShadow: 6,
            cursor: 'pointer',
            '& .download-icon': {
              color: 'primary.main',
            },
          },
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" component="h3">
            {title}
          </Typography>
        </Box>
        <IconButton
          className="download-icon"
          target="_blank"
          sx={{
            color: 'text.secondary',
            transition: 'color 0.2s',
          }}
        >
          <DescriptionIcon />
        </IconButton>
      </Card>
    </motion.div>
  );
}

function RegulationSection({ category, regulations }) {
  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          '& .MuiAccordionSummary-expandIconWrapper': {
            color: 'white'
          }
        }}
      >
        <Typography variant="h6">{category.name}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {Array.isArray(category.subCategories) && category.subCategories.map((subCat) => {
          const subCatRegulations = regulations.filter(
            r => r.categoryId === category.id && r.subCategory === subCat.name
          );

          if (subCatRegulations.length === 0) return null;

          return (
            <Box key={subCat.name} sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.main',
                  mb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  pb: 1,
                }}
              >
                {subCat.name}
              </Typography>
              <Grid container spacing={3}>
                {subCatRegulations.map((regulation) => (
                  <Grid item xs={12} md={6} key={regulation.id}>
                    <RegulationCard {...regulation} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}

function Regulations() {
  const { categoryId } = useParams();
  const status = useSelector(selectRegulationStatus);
  const regulations = useSelector(selectRegulations);
  const categories = useSelector(selectRegulationCategories);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {status !== 'succeeded' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      ) : (
        <>
          {!categoryId && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {'All Documents'}
              </Typography>
            </Box>
          )}
          {(categoryId ? categories.filter(cat => cat.id.toString() === categoryId.toString()) : categories).map((category) => (
            <RegulationSection
              key={category.id}
              category={category}
              regulations={regulations.filter(r => r.categoryId === category.id)}
            />
          ))}
        </>
      )}
    </Container>
  );
}

export default Regulations;
